import { type KeycloakTokenParsed, type KeycloakInitOptions, type KeycloakLogoutOptions } from 'keycloak-js';
import React, { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import keycloak from './keycloak';

export interface KeycloakContext {
  isAuthenticated: boolean;
  logout: (options: KeycloakLogoutOptions | undefined) => void;
  refreshToken: () => Promise<string | undefined>;
  hasRealmRole: (role: string) => boolean;
  tokenParsed?: KeycloakTokenParsed;
}

const defaultKeycloakContextValues: KeycloakContext = {
  isAuthenticated: false,
  logout: () => undefined,
  refreshToken: async () => undefined,
  hasRealmRole: () => false,
};

const KC_TOKEN_KEY = 'kc_token';
const KC_REFRESH_TOKEN_KEY = 'kc_refresh_token';
const KC_ID_TOKEN_KEY = 'kc_id_token';

const keycloakProviderInitOptions: KeycloakInitOptions = {
  onLoad: 'login-required',
  checkLoginIframe: false,
  token: sessionStorage.getItem(KC_TOKEN_KEY) ?? undefined,
  refreshToken: sessionStorage.getItem(KC_REFRESH_TOKEN_KEY) ?? undefined,
  idToken: sessionStorage.getItem(KC_ID_TOKEN_KEY) ?? undefined,
};

export const keycloakContext = createContext<KeycloakContext>(defaultKeycloakContextValues);

export interface KeycloakProviderProps {
  children: JSX.Element;
}

const VALID_TOKEN_SECONDS = 30;

const KeycloakProvider = (props: KeycloakProviderProps): JSX.Element => {
  const { children } = props;
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const { tokenParsed } = keycloak;

  const logout = useCallback((options: KeycloakLogoutOptions | undefined): void => {
    sessionStorage.removeItem(KC_TOKEN_KEY);
    sessionStorage.removeItem(KC_REFRESH_TOKEN_KEY);
    sessionStorage.removeItem(KC_ID_TOKEN_KEY);
    keycloak.logout(options);
  }, []);

  const refreshToken = useCallback(async (): Promise<string | undefined> => {
    await keycloak.updateToken(VALID_TOKEN_SECONDS);
    if (keycloak.token) sessionStorage.setItem(KC_TOKEN_KEY, keycloak.token);
    if (keycloak.refreshToken) sessionStorage.setItem(KC_REFRESH_TOKEN_KEY, keycloak.refreshToken);
    if (keycloak.idToken) sessionStorage.setItem(KC_ID_TOKEN_KEY, keycloak.idToken);
    return keycloak.token;
  }, []);

  const hasRealmRole = useCallback((role: string): boolean => keycloak.hasRealmRole(role), []);

  const initializeKeycloakCallback = useCallback(async (myKeycloakProviderInitOptions: KeycloakInitOptions) => {
    try {
      const myIsAuthenticated = await keycloak.init(myKeycloakProviderInitOptions);

      if (myIsAuthenticated) {
        sessionStorage.setItem(KC_TOKEN_KEY, keycloak.token ?? '');
        sessionStorage.setItem(KC_REFRESH_TOKEN_KEY, keycloak.refreshToken ?? '');
        sessionStorage.setItem(KC_ID_TOKEN_KEY, keycloak.idToken ?? '');
        setIsAuthenticated(true);
      }
    } catch (error) {
      setIsAuthenticated(false);
    }
  }, []);

  useEffect(() => {
    initializeKeycloakCallback(keycloakProviderInitOptions);
  }, [initializeKeycloakCallback]);

  const keycloakValue = useMemo(
    () => ({
      isAuthenticated,
      logout,
      refreshToken,
      hasRealmRole,
      tokenParsed,
    }),
    [isAuthenticated, logout, refreshToken, hasRealmRole, tokenParsed],
  );

  return <keycloakContext.Provider value={keycloakValue}>{children}</keycloakContext.Provider>;
};

const useKeycloak = (): KeycloakContext => React.useContext<KeycloakContext>(keycloakContext);

export { KeycloakProvider, useKeycloak };
